import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Plus, X, ChevronRight, ChevronLeft, Users, Calendar, Settings2, Grid2X2, Zap, Search, Archive, ArchiveRestore } from 'lucide-react';
import { fmtDate } from '../utils/dates';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { ymdTz } from '../utils/sportsUtils';
import { useBackdropClose } from '../utils/backdropClose';
import CalendarPicker from '../components/CalendarPicker';
import PayOptionsPicker from '../components/PayOptionsPicker';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0A0A0A',
  border: '1px solid #2A2A2A', color: '#fff', fontSize: 14, outline: 'none',
};
const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#737373', marginBottom: 6, display: 'block' };

const STATUS_COLORS = {
  draft: '#737373', registration_open: '#22C55E', registration_closed: '#F59E0B',
  in_progress: '#E63946', completed: '#3B82F6', cancelled: '#525252',
};
const STATUS_LABEL = {
  draft: 'Draft', registration_open: 'Registration Open', registration_closed: 'Registration Closed',
  in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled',
};

function Pill({ children, color }) {
  return (
    <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${color}22`, color }}>
      {children}
    </span>
  );
}

function OptionCard({ active, title, desc, icon: Icon, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} type="button"
      className="text-left p-4 rounded-xl transition-all w-full"
      style={{
        background: active ? 'rgba(230,57,70,0.12)' : '#111',
        border: `1px solid ${active ? '#E63946' : '#2A2A2A'}`,
      }}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4" style={{ color: active ? '#E63946' : '#A3A3A3' }} />}
        <span className="text-sm font-bold" style={{ color: '#fff' }}>{title}</span>
      </div>
      {desc && <p className="text-xs" style={{ color: '#737373' }}>{desc}</p>}
    </button>
  );
}

function CreateWizard({ courts, onClose, onCreated, facilityTz, announce = false }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const todayTz = ymdTz(facilityTz || 'Asia/Manila');
  const backdrop = useBackdropClose(onClose);
  const [form, setForm] = useState({
    name: '', description: '', date_start: '', date_end: '',
    format: 'single_elimination', game_mode: 'doubles', team_size: 2,
    scoring_target: 11, unlimited: false, win_by: 2, best_of: 1,
    entry_fee: '', max_teams: '', divisions: [], courts_used: [], third_place_match: false,
    payment_amount_options: ['full', 'deposit50', 'counter'],
    pool_play: false, pool_size: 4, advance_per_pool: 2,
    registration_mode: 'open', rsvp_auto_confirm: true, scorer_pin: '',
    rsvp_expire_hours_before: 48,
    contact_name: '', contact_email: '', contact_phone: '',
    // Announcement-style events
    announce_registration: 'external', registration_url: '', results_url: '',
    awards: [],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [divName, setDivName] = useState('');
  const [divMaxTeams, setDivMaxTeams] = useState('');

  const addDivision = () => {
    const n = divName.trim();
    if (!n) return;
    const max = divMaxTeams.trim() ? parseInt(divMaxTeams, 10) : null;
    set('divisions', [...form.divisions, { id: `tmp-${Date.now()}`, name: n, max_teams: max }]);
    setDivName(''); setDivMaxTeams('');
  };
  const removeDivision = (id) => set('divisions', form.divisions.filter(d => d.id !== id));
  const toggleCourt = (id) => set('courts_used',
    form.courts_used.includes(id) ? form.courts_used.filter(c => c !== id) : [...form.courts_used, id]);

  const STEPS = announce ? ['Basics', 'Registration', 'Divisions', 'Awards'] : ['Basics', 'Format', 'Scoring', 'Teams', 'Courts'];

  // Per-division awards (event-wide set when no divisions). Defaults are the
  // classic podium; owner edits labels/prizes freely, adds Sub Awards (MVP…).
  const defaultMain = () => ([{ rank: 1, label: 'Champion', prize: '' }, { rank: 2, label: '2nd Place', prize: '' }, { rank: 3, label: '3rd Place', prize: '' }]);
  const awardsFor = (divId) => form.awards.find(a => (a.division_id || null) === (divId || null)) || { division_id: divId || null, main: defaultMain(), sub: [] };
  const patchAwards = (divId, patch) => set('awards', [
    ...form.awards.filter(a => (a.division_id || null) !== (divId || null)),
    { ...awardsFor(divId), ...patch },
  ]);

  const next = () => {
    if (step === 1 && !form.name.trim()) { toast.error('Tournament name is required'); return; }
    setStep(s => Math.min(STEPS.length, s + 1));
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  const submit = async () => {
    if (announce && form.announce_registration === 'external' && !form.registration_url.trim()) {
      toast.error('Add the registration link, or switch to on-site registration');
      return;
    }
    setSaving(true);
    try {
      // Announce events: divisions keep their wizard ids so per-division awards
      // stay linked; every division group ships its awards (defaults included).
      const awardGroups = announce
        ? (form.divisions.length ? form.divisions : [{ id: null }]).map(d => awardsFor(d.id))
        : [];
      const payload = {
        ...form,
        event_style: announce ? 'announce' : 'full',
        awards: awardGroups,
        scoring_target: form.unlimited ? null : form.scoring_target,
        unlimited_points: form.unlimited,
        divisions: announce
          ? form.divisions.map(d => ({ id: d.id, name: d.name, max_teams: d.max_teams || null }))
          : form.divisions.map(d => ({ name: d.name, max_teams: d.max_teams || null })),
        registration_mode: form.registration_mode,
        rsvp_auto_confirm: form.rsvp_auto_confirm,
        rsvp_expire_hours_before: form.rsvp_expire_hours_before,
        scorer_pin: form.scorer_pin || null,
      };
      const res = await sportsApi.post('/sports/tournaments', payload);
      toast.success('Tournament saved as Draft — click "Open Registration" when you\'re ready to go live', { duration: 5000 });
      onCreated(res.data);
    } catch (e) {
      if (e.response?.status === 402) {
        const msg = e.response?.data?.detail?.message || e.response?.data?.detail || '';
        toast.error(msg || 'Trial limit reached. Contact 09952568450 to upgrade.', { duration: 8000 });
      } else {
        const d = e.response?.data?.detail;
        toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to create tournament');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }} {...backdrop}>
      <div data-testid="tournament-wizard" className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #2A2A2A' }}>
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5" style={{ color: '#E63946' }} />
            <span className="text-base font-bold" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{announce ? 'ANNOUNCE A TOURNAMENT' : 'START A TOURNAMENT'}</span>
          </div>
          <button onClick={onClose} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 px-5 pt-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className="h-1 rounded-full transition-all" style={{ background: i + 1 <= step ? '#E63946' : '#2A2A2A' }} />
              <p className="text-xs mt-1" style={{ color: i + 1 === step ? '#fff' : '#525252' }}>{s}</p>
            </div>
          ))}
        </div>

        <div className="p-5 space-y-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {step === 1 && (
            <>
              <div>
                <label style={labelStyle}>Tournament Name *</label>
                <input data-testid="tourn-name" autoFocus value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Summer Pickleball Open" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} placeholder="Optional details for players" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <CalendarPicker testid="tourn-start" value={form.date_start} min={todayTz}
                    onChange={v => {
                      set('date_start', v);
                      if (form.date_end && v > form.date_end) set('date_end', v);
                    }}
                    placeholder="Pick start date" />
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <CalendarPicker testid="tourn-end" value={form.date_end} min={form.date_start || todayTz}
                    onChange={v => set('date_end', v)} placeholder="Pick end date" />
                  {form.date_start && form.date_end && form.date_end < form.date_start && (
                    <p style={{ fontSize: 11, color: '#FF2D55', marginTop: 4 }}>End date must be on or after start date</p>
                  )}
                </div>
              </div>

              {/* Tournament contact person — used as reply-to on invitations & shown to players */}
              <div className="rounded-xl p-3" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                <label style={{ ...labelStyle, color: '#E63946' }}>Tournament Contact Person</label>
                <p style={{ fontSize: 11, color: '#737373', margin: '2px 0 8px' }}>Shown to players and set as the reply-to on invitation emails, so questions reach the right person.</p>
                <input data-testid="tourn-contact-name" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Contact name (e.g. Coach Mike)" style={inputStyle} />
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <input data-testid="tourn-contact-email" type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="Contact email" style={inputStyle} />
                  <input data-testid="tourn-contact-phone" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} placeholder="Contact number" style={inputStyle} />
                </div>
              </div>
            </>
          )}

          {step === 2 && announce && (
            <>
              <div>
                <label style={labelStyle}>How do players register?</label>
                <div className="grid grid-cols-1 gap-2">
                  <OptionCard testid="announce-reg-external" active={form.announce_registration === 'external'}
                    title="External site" desc="Registration runs on another app (Reclub, Google Forms, …) — you paste the link, we generate a QR for posters."
                    onClick={() => set('announce_registration', 'external')} />
                  <OptionCard testid="announce-reg-onsite" active={form.announce_registration === 'onsite'}
                    title="On-site / here" desc="Take registrations on your public page with the regular team sign-up (payments, proof, player lists)."
                    onClick={() => set('announce_registration', 'onsite')} />
                  <OptionCard testid="announce-reg-none" active={form.announce_registration === 'none'}
                    title="Announcement only" desc="No sign-up — just tell people it's happening."
                    onClick={() => set('announce_registration', 'none')} />
                </div>
              </div>
              {form.announce_registration === 'external' && (
                <div>
                  <label style={labelStyle}>Registration link *</label>
                  <input data-testid="announce-reg-url" value={form.registration_url}
                    onChange={e => set('registration_url', e.target.value)}
                    placeholder="https://reclub.app/your-tournament" style={inputStyle} />
                </div>
              )}
              {/* Entry fee is public information regardless of WHERE registration
                  happens — external events state it too (owner 2026-08-17). */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>Entry fee (₱)</label>
                  <input data-testid="announce-fee" type="number" min="0" value={form.entry_fee}
                    onChange={e => set('entry_fee', e.target.value)} placeholder="0" style={inputStyle} />
                  <p className="text-xs mt-1" style={{ color: '#737373' }}>Shown on your public page{form.announce_registration === 'external' ? ' — collected on the external site' : ''}.</p>
                </div>
                {form.announce_registration === 'onsite' && (
                  <div>
                    <label style={labelStyle}>Max teams</label>
                    <input data-testid="announce-max-teams" type="number" min="2" value={form.max_teams}
                      onChange={e => set('max_teams', e.target.value)} placeholder="No limit" style={inputStyle} />
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Results link (optional)</label>
                <input data-testid="announce-results-url" value={form.results_url}
                  onChange={e => set('results_url', e.target.value)}
                  placeholder="https://… — add it later if results live on another site" style={inputStyle} />
                <p className="text-xs mt-1" style={{ color: '#737373' }}>Shown as "View results" on your public page. You can add or change it any time.</p>
              </div>
            </>
          )}

          {step === 3 && announce && (
            <>
              <div>
                <label style={labelStyle}>Divisions (optional)</label>
                <p className="text-xs mb-2" style={{ color: '#737373' }}>e.g. Open, Intermediate, Beginner — each division gets its own awards on the next step. Skip this for a single-bracket event.</p>
                <div className="flex gap-2 mb-2">
                  <input data-testid="announce-div-name" value={divName} onChange={e => setDivName(e.target.value)} placeholder="Division name" style={{ ...inputStyle, flex: 2 }} />
                  <input data-testid="announce-div-max" type="number" min="1" value={divMaxTeams} onChange={e => setDivMaxTeams(e.target.value)} placeholder="Max teams" style={{ ...inputStyle, flex: 1 }} />
                  <button data-testid="announce-div-add" onClick={addDivision} className="px-4 rounded-lg text-sm font-bold" style={{ background: '#E63946', color: '#fff' }}>Add</button>
                </div>
                {form.divisions.map(d => (
                  <div key={d.id} className="flex items-center justify-between rounded-lg px-3 py-2 mb-1" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                    <span className="text-sm" style={{ color: '#fff' }}>{d.name}{d.max_teams ? ` · max ${d.max_teams}` : ''}</span>
                    <button onClick={() => removeDivision(d.id)} style={{ color: '#737373' }}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 4 && announce && (
            <>
              {(form.divisions.length ? form.divisions : [{ id: null, name: 'Event Awards' }]).map(d => {
                const aw = awardsFor(d.id);
                const setMain = (list) => patchAwards(d.id, { main: list });
                const setSub = (list) => patchAwards(d.id, { sub: list });
                return (
                  <div key={d.id || 'all'} className="rounded-xl p-3 space-y-2" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A' }}>
                    <p className="text-sm font-bold uppercase" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>{d.name}</p>
                    <label style={labelStyle}>Main awards</label>
                    {aw.main.map((m, i) => (
                      <div key={i} className="flex gap-2">
                        <input value={m.label} onChange={e => setMain(aw.main.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                          placeholder={`Place ${i + 1}`} style={{ ...inputStyle, flex: 1 }} />
                        <input value={m.prize} onChange={e => setMain(aw.main.map((x, xi) => xi === i ? { ...x, prize: e.target.value } : x))}
                          placeholder="Prize — e.g. ₱5,000 + Arronax Ball sponsorship" style={{ ...inputStyle, flex: 2 }} />
                        <button onClick={() => setMain(aw.main.filter((_, xi) => xi !== i))} style={{ color: '#737373' }}><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                    <button onClick={() => setMain([...aw.main, { rank: aw.main.length + 1, label: '', prize: '' }])}
                      className="text-xs font-bold" style={{ color: '#E63946' }}>+ Add main award</button>
                    <label style={labelStyle}>Sub awards (MVP, Smasher, …)</label>
                    {aw.sub.map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <input value={s.label} onChange={e => setSub(aw.sub.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                          placeholder="e.g. MVP" style={{ ...inputStyle, flex: 1 }} />
                        <input value={s.prize} onChange={e => setSub(aw.sub.map((x, xi) => xi === i ? { ...x, prize: e.target.value } : x))}
                          placeholder="Prize (optional)" style={{ ...inputStyle, flex: 2 }} />
                        <button onClick={() => setSub(aw.sub.filter((_, xi) => xi !== i))} style={{ color: '#737373' }}><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                    <button data-testid={`announce-add-sub-${d.id || 'all'}`} onClick={() => setSub([...aw.sub, { label: '', prize: '' }])}
                      className="text-xs font-bold" style={{ color: '#E63946' }}>+ Add sub award</button>
                  </div>
                );
              })}
              <p className="text-xs" style={{ color: '#737373' }}>Prizes show on your public page right away. You declare the winners from the Awards tab once the tournament finishes.</p>
            </>
          )}

          {step === 2 && !announce && (
            <>
              <div>
                <label style={labelStyle}>Format</label>
                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <OptionCard testid="fmt-single" active={form.format === 'single_elimination' && !form.pool_play} title="Single Elimination"
                      desc="Lose once and you're out. Winner auto-advances through the bracket." onClick={() => { set('format', 'single_elimination'); set('pool_play', false); }} />
                    {/* 3rd-place sub-option — only visible under Single Elimination, clearly nested */}
                    {form.format === 'single_elimination' && !form.pool_play && (
                      <div style={{ marginLeft: 14, borderLeft: '2px solid #CD7F3230', paddingLeft: 12, marginTop: 3 }}>
                        <button type="button" data-testid="third-place-toggle"
                          onClick={() => set('third_place_match', !form.third_place_match)}
                          className="flex items-center gap-2.5 w-full text-left py-2 px-2.5 rounded-lg transition-all"
                          style={{ background: form.third_place_match ? 'rgba(205,127,50,0.10)' : 'transparent', border: `1px solid ${form.third_place_match ? '#CD7F3260' : '#1E1E1E'}` }}>
                          <div style={{
                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                            border: `2px solid ${form.third_place_match ? '#CD7F32' : '#444'}`,
                            background: form.third_place_match ? '#CD7F32' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {form.third_place_match && <span style={{ color: '#0A0A0A', fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                          </div>
                          <div>
                            <span className="text-xs font-semibold" style={{ color: form.third_place_match ? '#CD7F32' : '#A3A3A3' }}>Include 3rd-Place Match</span>
                            <p className="text-[10px]" style={{ color: '#555' }}>Losing semifinalists play a bronze medal match</p>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  <OptionCard testid="fmt-double" active={form.format === 'double_elimination' && !form.pool_play} title="Double Elimination"
                    desc="Lose twice to be out. Losers drop to a 2nd bracket; both meet at a Grand Final (fishbone)." onClick={() => { set('format', 'double_elimination'); set('pool_play', false); }} />
                  <OptionCard testid="fmt-rr" active={form.format === 'round_robin' && !form.pool_play} title="Round Robin"
                    desc="Everyone plays everyone. Ranked by wins, then point differential." onClick={() => { set('format', 'round_robin'); set('pool_play', false); }} />
                  <div>
                    <OptionCard testid="fmt-pool" active={form.pool_play} title="Pool → Knockout"
                      desc="Teams split into round-robin pools; the top finishers advance into a seeded single-elimination bracket. Fairer (everyone plays several games) and no manual seeding." onClick={() => { set('pool_play', true); set('format', 'single_elimination'); }} />
                    {form.pool_play && (
                      <div className="mt-2 p-3 rounded-lg grid grid-cols-2 gap-3" style={{ background: 'rgba(230,57,70,0.06)', border: '1px solid #2A1A1C' }}>
                        <div>
                          <label className="text-xs" style={{ color: '#A3A3A3' }}>Target pool size</label>
                          <select data-testid="pool-size" value={form.pool_size || 4} onChange={e => set('pool_size', Number(e.target.value))} style={inputStyle}>
                            {[3, 4, 5, 6].map(n => <option key={n} value={n}>{n} teams / pool</option>)}
                          </select>
                          <p className="text-[10px] mt-1" style={{ color: '#666' }}>Number of pools is decided at generation from actual turnout.</p>
                        </div>
                        <div>
                          <label className="text-xs" style={{ color: '#A3A3A3' }}>Advance per pool</label>
                          <select data-testid="advance-per-pool" value={form.advance_per_pool || 2} onChange={e => set('advance_per_pool', Number(e.target.value))} style={inputStyle}>
                            {[1, 2, 3, 4].map(n => <option key={n} value={n}>Top {n}</option>)}
                          </select>
                          <button type="button" onClick={() => set('third_place_match', !form.third_place_match)}
                            className="flex items-center gap-1.5 mt-2 text-xs" style={{ color: form.third_place_match ? '#CD7F32' : '#A3A3A3' }}>
                            <span style={{ width: 14, height: 14, borderRadius: 3, border: `2px solid ${form.third_place_match ? '#CD7F32' : '#444'}`, background: form.third_place_match ? '#CD7F32' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {form.third_place_match && <span style={{ color: '#0A0A0A', fontSize: 9, fontWeight: 900 }}>✓</span>}
                            </span>
                            3rd-place match
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Team Size</label>
                <div className="grid grid-cols-4 gap-2">
                  <OptionCard testid="mode-singles" active={form.game_mode === 'singles'} title="Singles" desc="1 player" onClick={() => { set('game_mode', 'singles'); set('team_size', 1); }} />
                  <OptionCard testid="mode-doubles" active={form.game_mode === 'doubles'} title="Doubles" desc="2 players" onClick={() => { set('game_mode', 'doubles'); set('team_size', 2); }} />
                  <OptionCard testid="mode-mixed" active={form.game_mode === 'mixed_doubles'} title="Mixed" desc="2 players" onClick={() => { set('game_mode', 'mixed_doubles'); set('team_size', 2); }} />
                  <OptionCard testid="mode-custom" active={form.game_mode === 'custom'} title="Custom" desc="N per team" onClick={() => set('game_mode', 'custom')} />
                </div>
                {form.game_mode === 'custom' && (
                  <div className="mt-2">
                    <label style={labelStyle}>Players per team (e.g. 5 basketball, 7 frisbee, 11 soccer)</label>
                    <input data-testid="team-size-input" type="number" min="1" max="20" value={form.team_size}
                      onChange={e => set('team_size', parseInt(e.target.value) || 1)} style={inputStyle} />
                  </div>
                )}
              </div>
            </>
          )}

          {step === 3 && !announce && (
            <>
              <div>
                <label style={labelStyle}>Points to Win a Game</label>
                <button type="button" data-testid="unlimited-toggle" onClick={() => set('unlimited', !form.unlimited)}
                  className="w-full mb-2 p-3 rounded-xl text-left transition-all"
                  style={{ background: form.unlimited ? 'rgba(230,57,70,0.12)' : '#111', border: `1px solid ${form.unlimited ? '#E63946' : '#2A2A2A'}` }}>
                  <span className="text-sm font-bold" style={{ color: '#fff' }}>∞ No target / unlimited</span>
                  <p className="text-xs" style={{ color: '#737373' }}>For basketball, soccer, etc. The referee ends each game manually on the scorer.</p>
                </button>
                {!form.unlimited && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {[11, 15, 21].map(v => (
                        <OptionCard key={v} testid={`target-${v}`} active={form.scoring_target === v} title={`${v} pts`} onClick={() => set('scoring_target', v)} />
                      ))}
                    </div>
                    <input type="number" min="1" value={form.scoring_target} onChange={e => set('scoring_target', parseInt(e.target.value) || 1)} style={{ ...inputStyle, marginTop: 8 }} placeholder="Custom target" />
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>Win By</label>
                  <select value={form.win_by} onChange={e => set('win_by', parseInt(e.target.value))} disabled={form.unlimited} style={{ ...inputStyle, cursor: 'pointer', opacity: form.unlimited ? 0.5 : 1 }}>
                    <option value={1}>1 point</option>
                    <option value={2}>2 points</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Best Of</label>
                  <select data-testid="best-of" value={form.best_of} onChange={e => set('best_of', parseInt(e.target.value))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value={1}>1 game</option>
                    <option value={3}>3 games</option>
                    <option value={5}>5 games</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {step === 4 && !announce && (
            <>
              <div>
                <label style={labelStyle}>Registration Mode</label>
                <div className="grid grid-cols-1 gap-2">
                  <OptionCard testid="reg-open" active={form.registration_mode === 'open'} title="Open Registration"
                    desc="Anyone can register via the public link or QR code. Standard tournament flow." onClick={() => set('registration_mode', 'open')} />
                  <OptionCard testid="reg-mixed" active={form.registration_mode === 'mixed'} title="Mixed (Open + Invitations)"
                    desc="Open for public registration AND you can send RSVP invitations to specific teams. Invited teams get reserved slots." onClick={() => set('registration_mode', 'mixed')} />
                  <OptionCard testid="reg-invite" active={form.registration_mode === 'invite_only'} title="Invite Only"
                    desc="Only teams you invite via email RSVP can join. No public registration. Full control over who plays." onClick={() => set('registration_mode', 'invite_only')} />
                </div>
              </div>
              {form.registration_mode !== 'open' && (
                <div>
                  <label style={labelStyle}>When a team accepts the RSVP</label>
                  <div className="grid grid-cols-1 gap-2">
                    <OptionCard testid="rsvp-auto" active={form.rsvp_auto_confirm} title="Auto-Confirm"
                      desc="Accepting the invitation instantly registers the team. Entry fee (if any) can be paid at the venue." onClick={() => set('rsvp_auto_confirm', true)} />
                    <OptionCard testid="rsvp-manual" active={!form.rsvp_auto_confirm} title="Require Payment First"
                      desc="Team must pay the entry fee before they are fully confirmed. Good for paid tournaments." onClick={() => set('rsvp_auto_confirm', false)} />
                  </div>
                </div>
              )}
              {form.registration_mode !== 'open' && (
                <div>
                  <label style={labelStyle}>Auto-Cancel Unresponded Invitations</label>
                  <select data-testid="rsvp-expire" value={form.rsvp_expire_hours_before ?? ''}
                    onChange={e => set('rsvp_expire_hours_before', e.target.value === '' ? null : Number(e.target.value))}
                    style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">Never auto-cancel</option>
                    <option value="6">6 hours before event</option>
                    <option value="12">12 hours before event</option>
                    <option value="24">1 day before event</option>
                    <option value="48">2 days before event</option>
                    <option value="72">3 days before event</option>
                    <option value="168">1 week before event</option>
                  </select>
                  <p className="text-xs mt-1" style={{ color: '#737373' }}>Invitations still pending at this cutoff are automatically marked expired, freeing the slot. Choose "Never" if you want manual control.</p>
                </div>
              )}
              <div>
                <label style={labelStyle}>Max Teams (leave blank for unlimited)</label>
                <input data-testid="max-teams" type="number" min="2" value={form.max_teams} onChange={e => set('max_teams', e.target.value)} placeholder="e.g. 16" style={inputStyle} />
                <p className="text-xs mt-1" style={{ color: '#737373' }}>Registration auto-closes when this limit is reached.</p>
              </div>
              <div>
                <label style={labelStyle}>Entry Fee (₱ per team, blank = free)</label>
                <input data-testid="entry-fee" type="number" min="0" step="0.01" value={form.entry_fee} onChange={e => set('entry_fee', e.target.value)} placeholder="e.g. 500" style={inputStyle} />
                <p className="text-xs mt-1" style={{ color: '#737373' }}>When set, public registrants pay via your GCash QR (with proof) or choose Pay at Venue.</p>
              </div>
              {parseFloat(form.entry_fee || 0) > 0 && (
                <div>
                  <label style={labelStyle}>Payment Options for Registrants</label>
                  <PayOptionsPicker value={form.payment_amount_options} testidPrefix="tn-payopt"
                    onChange={v => set('payment_amount_options', v)} />
                  <p className="text-xs mt-1" style={{ color: '#737373' }}>Choose how teams can settle the entry fee. Turning off "Pay at Venue" forces upfront payment.</p>
                </div>
              )}
              <div>
                <label style={labelStyle}>Divisions (optional)</label>
                <div className="flex gap-2">
                  <input value={divName} onChange={e => setDivName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDivision())}
                    placeholder="e.g. Beginners, Advanced, 50+" style={{ ...inputStyle, flex: 2 }} />
                  <input value={divMaxTeams} onChange={e => setDivMaxTeams(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDivision())}
                    type="number" min="2" placeholder="Max teams" style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={addDivision} className="px-4 rounded-lg text-sm font-bold" style={{ background: '#E63946', color: '#fff', flexShrink: 0 }}>Add</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.divisions.map(d => (
                    <span key={d.id} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full" style={{ background: '#1A1A1A', color: '#E5E5E5', border: '1px solid #2A2A2A' }}>
                      {d.name}{d.max_teams ? <span style={{ color: '#E63946', marginLeft: 2 }}>— max {d.max_teams}</span> : ''}
                      <button onClick={() => removeDivision(d.id)} style={{ color: '#737373' }}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: '#737373' }}>Set a max teams cap per division to control registration balance. Leave max blank for unlimited.</p>
              </div>
            </>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Courts Used</label>
                {courts.length === 0 ? (
                  <p className="text-sm" style={{ color: '#737373' }}>No courts found. You can assign courts later.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {courts.map(c => (
                      <OptionCard key={c.id} testid={`court-${c.id}`} active={form.courts_used.includes(c.id)} title={c.name} desc={c.sport} icon={Grid2X2} onClick={() => toggleCourt(c.id)} />
                    ))}
                  </div>
                )}
                <p className="text-xs mt-2" style={{ color: '#737373' }}>Matches will be auto-assigned to these courts as they become available.</p>
              </div>
              <div>
                <label style={labelStyle}>Scorer PIN (optional)</label>
                <input data-testid="scorer-pin" type="text" inputMode="numeric" maxLength={6} value={form.scorer_pin || ''}
                  onChange={e => set('scorer_pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="e.g. 1234" style={inputStyle} />
                <p className="text-xs mt-1" style={{ color: '#737373' }}>4-6 digit PIN to lock scoring to one device per match. Referees use this PIN to take over scoring from another device.</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4" style={{ borderTop: '1px solid #2A2A2A' }}>
          <button onClick={step === 1 ? onClose : back} className="flex items-center gap-1 text-sm" style={{ color: '#A3A3A3' }}>
            {step === 1 ? 'Cancel' : <><ChevronLeft className="h-4 w-4" /> Back</>}
          </button>
          {step < STEPS.length ? (
            <button data-testid="wizard-next" onClick={next} className="flex items-center gap-1 px-5 py-2 rounded-lg text-sm font-bold" style={{ background: '#E63946', color: '#fff' }}>
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="wizard-create" onClick={submit} disabled={saving} className="px-6 py-2 rounded-lg text-sm font-bold uppercase" style={{ background: '#E63946', color: '#fff', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Creating…' : (announce ? 'Announce Tournament' : 'Create Tournament')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SportsTournamentsPage() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [openPlaySessions, setOpenPlaySessions] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [announceMode, setAnnounceMode] = useState(false);
  const [showChooser, setShowChooser] = useState(false);
  const [fullEnabled, setFullEnabled] = useState(false);
  const [facilityTz, setFacilityTz] = useState('Asia/Manila');
  const [query, setQuery] = useState('');
  const [filterChip, setFilterChip] = useState('all');

  const fetchData = () => {
    Promise.all([
      sportsApi.get('/sports/tournaments'),
      sportsApi.get('/sports/courts'),
      sportsApi.get('/sports/settings'),
      sportsApi.get('/sports/open-play').catch(() => ({ data: [] })),
    ]).then(([t, c, s, op]) => {
      setTournaments(t.data || []);
      setCourts(c.data || []);
      setFacilityTz(s.data?.timezone || 'Asia/Manila');
      setOpenPlaySessions(op.data || []);
    }).catch(() => toast.error('Failed to load tournaments')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // Whether full bracket-managed tournaments can be created (platform-gated
  // while debugged internally). Announce-style events are always available.
  useEffect(() => {
    sportsApi.get('/sports/tournaments-config')
      .then(r => setFullEnabled(!!r.data?.full_tournaments_enabled))
      .catch(() => setFullEnabled(false));
  }, []);

  // Merged event list: tournaments + open play sessions
  const ts = (v) => { const t = Date.parse(v || ''); return Number.isNaN(t) ? 0 : t; };
  const events = [
    ...tournaments.map(t => ({ kind: 'tournament', sort: ts(t.created_at || t.date_start), data: t })),
    ...openPlaySessions.map(s => ({ kind: 'open_play', sort: ts(s.created_at || s.date), data: s })),
  ];

  // Status grouping + priority ranking (live pinned first)
  const groupOf = (ev) => {
    if (ev.data.archived) return 'archived';
    const s = ev.data.status;
    if (s === 'in_progress' || s === 'active') return 'live';
    if (s === 'registration_open') return 'open';
    if (s === 'completed' || s === 'ended') return 'completed';
    if (s === 'draft') return 'draft';
    return 'other';
  };
  const RANK = { live: 0, open: 1, other: 2, draft: 3, completed: 4, archived: 5 };
  const chipCounts = events.reduce((acc, ev) => { const g = groupOf(ev); acc[g] = (acc[g] || 0) + 1; return acc; }, {});
  const CHIPS = [
    { id: 'all', label: 'All', count: events.length - (chipCounts.archived || 0) },
    { id: 'live', label: 'Live', count: chipCounts.live || 0, color: '#E63946' },
    { id: 'open', label: 'Registration Open', count: chipCounts.open || 0, color: '#22C55E' },
    { id: 'completed', label: 'Completed', count: chipCounts.completed || 0, color: '#3B82F6' },
    { id: 'draft', label: 'Draft', count: chipCounts.draft || 0, color: '#737373' },
    { id: 'archived', label: 'Archived', count: chipCounts.archived || 0, color: '#525252' },
  ];
  const visibleEvents = events
    .filter(ev => filterChip === 'all' ? groupOf(ev) !== 'archived' : groupOf(ev) === filterChip)
    .filter(ev => !query.trim() || (ev.data.name || '').toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => (RANK[groupOf(a)] - RANK[groupOf(b)]) || (b.sort - a.sort));

  const toggleArchive = async (ev) => {
    const next = !ev.data.archived;
    try {
      await sportsApi.post(
        ev.kind === 'tournament' ? `/sports/tournaments/${ev.data.id}/archive` : `/sports/open-play/${ev.data.id}/archive`,
        { archived: next });
      toast.success(next ? 'Event archived — data is preserved' : 'Event restored');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <SportsNavBar />
      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
              <Trophy className="h-6 w-6" style={{ color: '#E63946' }} /> TOURNAMENTS & EVENTS
            </h1>
            <p className="text-sm mt-1" style={{ color: '#737373' }}>Formal brackets or social Open Play rotations — create and run them all here.</p>
          </div>
          <button data-testid="start-tournament-btn" onClick={() => setShowChooser(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold uppercase" style={{ background: '#E63946', color: '#fff' }}>
            <Plus className="h-4 w-4" /> Create Event
          </button>
        </div>

        {loading ? (
          <p style={{ color: '#737373' }}>Loading…</p>
        ) : events.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
            <Trophy className="h-10 w-10 mx-auto mb-3" style={{ color: '#2A2A2A' }} />
            <p className="text-sm" style={{ color: '#A3A3A3' }}>No events yet. Create your first tournament or open play session!</p>
          </div>
        ) : (
          <>
            {/* Search + status filter chips */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#525252' }} />
                <input data-testid="events-search-input" value={query} onChange={e => setQuery(e.target.value)}
                  placeholder="Search events…"
                  className="w-full rounded-lg text-sm"
                  style={{ padding: '9px 12px 9px 36px', background: '#141414', border: '1px solid #262626', color: '#fff', outline: 'none' }} />
              </div>
              <div className="flex gap-1.5 flex-wrap items-center">
                {CHIPS.filter(c => c.id === 'all' || c.count > 0).map(c => (
                  <button key={c.id} data-testid={`events-filter-${c.id}`} onClick={() => setFilterChip(c.id)}
                    className="px-3 py-1.5 rounded-full text-xs font-bold uppercase"
                    style={{
                      background: filterChip === c.id ? (c.color || '#E63946') + '22' : '#141414',
                      color: filterChip === c.id ? (c.color || '#fff') : '#737373',
                      border: `1px solid ${filterChip === c.id ? (c.color || '#E63946') : '#262626'}`,
                      fontFamily: 'Oswald, sans-serif',
                    }}>
                    {c.label} <span style={{ opacity: 0.7 }}>({c.count})</span>
                  </button>
                ))}
              </div>
            </div>

            {visibleEvents.length === 0 ? (
              <div className="rounded-2xl p-10 text-center" style={{ background: '#0F0F0F', border: '1px dashed #2A2A2A' }}>
                <p className="text-sm" style={{ color: '#737373' }}>No events match this filter.</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {visibleEvents.map(ev => ev.kind === 'tournament' ? (
                  <TournamentCard key={`t-${ev.data.id}`} t={ev.data} navigate={navigate} onArchive={() => toggleArchive(ev)} />
                ) : (
                  <OpenPlayCard key={`op-${ev.data.id}`} s={ev.data} navigate={navigate} onArchive={() => toggleArchive(ev)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showChooser && (
        <EventTypeChooser
          fullEnabled={fullEnabled}
          onClose={() => setShowChooser(false)}
          onTournament={() => { setShowChooser(false); setAnnounceMode(false); setShowWizard(true); }}
          onAnnounce={() => { setShowChooser(false); setAnnounceMode(true); setShowWizard(true); }}
          onOpenPlay={() => { setShowChooser(false); navigate('/sports/open-play/new'); }}
        />
      )}
      {showWizard && (
        <CreateWizard courts={courts} facilityTz={facilityTz} announce={announceMode} onClose={() => setShowWizard(false)}
          onCreated={(t) => { setShowWizard(false); navigate(`/sports/tournaments/${t.id}`); }} />
      )}
    </div>
  );
}

function EventTypeChooser({ onClose, onTournament, onAnnounce, onOpenPlay, fullEnabled }) {
  const backdrop = useBackdropClose(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}
      {...backdrop} data-testid="event-type-chooser">
      <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#141414', border: '1px solid #333' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>What are you running?</h3>
          <button onClick={onClose} data-testid="chooser-close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs mb-5" style={{ color: '#737373' }}>Pick the event type — you can run more than one side by side.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button data-testid="choose-announce" onClick={onAnnounce}
            className="text-left p-5 rounded-xl transition-all hover:scale-[1.01]"
            style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <Trophy className="h-7 w-7 mb-3" style={{ color: '#E63946' }} />
            <p className="text-sm font-black uppercase mb-1" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>Tournament</p>
            <p className="text-xs" style={{ color: '#737373' }}>Announce your tournament on your public page — registration via an external link (with QR) or on-site sign-ups, then post the winners and awards.</p>
          </button>
          {fullEnabled && (
            <button data-testid="choose-tournament" onClick={onTournament}
              className="text-left p-5 rounded-xl transition-all hover:scale-[1.01]"
              style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
              <Grid2X2 className="h-7 w-7 mb-3" style={{ color: '#3B82F6' }} />
              <p className="text-sm font-black uppercase mb-1" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>Bracket-Managed Tournament</p>
              <p className="text-xs" style={{ color: '#737373' }}>Fixed teams competing on a bracket — single/double elimination, round robin, or pool play. Seeding, standings, and a champion.</p>
            </button>
          )}
          <button data-testid="choose-open-play" onClick={onOpenPlay}
            className="text-left p-5 rounded-xl transition-all hover:scale-[1.01]"
            style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <Zap className="h-7 w-7 mb-3" style={{ color: '#F59E0B' }} />
            <p className="text-sm font-black uppercase mb-1" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>Open Play</p>
            <p className="text-xs" style={{ color: '#737373' }}>Social rotation — individuals check in and the smart queue mixes partners and opponents every game. Great for club nights.</p>
          </button>
        </div>
      </div>
    </div>
  );
}

const OP_STATUS = {
  draft: { color: '#737373', label: 'Draft' },
  registration_open: { color: '#22C55E', label: 'Registration Open' },
  active: { color: '#E63946', label: 'LIVE' },
  ended: { color: '#3B82F6', label: 'Ended' },
};

function ArchiveBtn({ archived, onArchive, testid }) {
  const Icon = archived ? ArchiveRestore : Archive;
  return (
    <button data-testid={testid} title={archived ? 'Restore event' : 'Archive event (data preserved)'}
      onClick={e => { e.stopPropagation(); onArchive(); }}
      className="p-1.5 rounded-lg shrink-0"
      style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373' }}>
      <Icon className="h-4 w-4" />
    </button>
  );
}

function OpenPlayCard({ s, navigate, onArchive }) {
  const st = OP_STATUS[s.status] || OP_STATUS.draft;
  const archivable = s.archived || s.status === 'ended';
  return (
    <div data-testid={`open-play-card-${s.id}`} role="button" tabIndex={0} onClick={() => navigate(`/sports/open-play/${s.id}`)}
      className="text-left p-5 rounded-xl transition-all hover:scale-[1.005] cursor-pointer" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold" style={{ color: '#fff' }}>{s.name}</h3>
            <Pill color="#F59E0B">Open Play</Pill>
            <Pill color={st.color}>{st.label}</Pill>
            {s.archived && <Pill color="#525252">Archived</Pill>}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs mt-2" style={{ color: '#737373' }}>
            <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> {s.game_mode === 'singles' ? 'Singles' : 'Doubles'} rotation</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {s.player_count || 0} players</span>
            {parseFloat(s.entry_fee || 0) > 0 && <span style={{ color: '#22C55E' }}>₱{s.entry_fee} entry</span>}
            {s.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(s.date)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {archivable && <ArchiveBtn archived={!!s.archived} onArchive={onArchive} testid={`archive-op-${s.id}`} />}
          <ChevronRight className="h-5 w-5" style={{ color: '#525252' }} />
        </div>
      </div>
    </div>
  );
}

function TournamentCard({ t, navigate, onArchive }) {
  const archivable = t.archived || t.status === 'completed' || t.status === 'cancelled';
  return (
    <div data-testid={`tournament-card-${t.id}`} role="button" tabIndex={0} onClick={() => navigate(`/sports/tournaments/${t.id}`)}
      className="text-left p-5 rounded-xl transition-all hover:scale-[1.005] cursor-pointer" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold" style={{ color: '#fff' }}>{t.name}</h3>
            <Pill color={STATUS_COLORS[t.status]}>{STATUS_LABEL[t.status]}</Pill>
            {t.archived && <Pill color="#525252">Archived</Pill>}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs mt-2" style={{ color: '#737373' }}>
            {t.event_style === 'announce' ? (
              <span className="flex items-center gap-1"><Settings2 className="h-3 w-3" /> Announcement{t.announce_registration === 'external' ? ' · external sign-up' : t.announce_registration === 'onsite' ? ' · on-site sign-up' : ''}</span>
            ) : (
              <span className="flex items-center gap-1"><Settings2 className="h-3 w-3" /> {t.format === 'round_robin' ? 'Round Robin' : t.format === 'double_elimination' ? 'Double Elim' : 'Single Elim'}</span>
            )}
            {(t.event_style !== 'announce' || t.announce_registration === 'onsite') && (
              <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {t.team_count} teams{t.max_teams ? ` / ${t.max_teams}` : ''}</span>
            )}
            {t.event_style !== 'announce' && <span>{t.game_mode === 'custom' ? `${t.team_size}v${t.team_size}` : t.game_mode.replace('_', ' ')}</span>}
            {t.event_style !== 'announce' && <span>Best of {t.best_of}, to {t.scoring_target || '∞'}</span>}
            {t.entry_fee > 0 && <span style={{ color: '#22C55E' }}>₱{t.entry_fee} entry</span>}
            {t.date_start && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(t.date_start)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {archivable && <ArchiveBtn archived={!!t.archived} onArchive={onArchive} testid={`archive-tournament-${t.id}`} />}
          <ChevronRight className="h-5 w-5" style={{ color: '#525252' }} />
        </div>
      </div>
    </div>
  );
}
